import { z } from 'zod'

export const searchContactsSchema = z.object({
  term: z.string().min(1).describe('Name, phone number, or email to search for'),
  page: z.number().int().min(1).default(1),
  per_page: z.number().int().min(1).max(50).default(10),
})

export const getContactSchema = z.object({
  id: z.number().int().describe('Contact id'),
})

export const openContactSchema = z.object({
  // Coerce: the id may arrive as a string from prior tool output; accept both.
  contact_id: z.coerce
    .number()
    .int()
    .describe('Contact id to open (from search_contacts / search_contacts_list)'),
})

export const searchContactsListSchema = z.object({
  term: z
    .string()
    .default('')
    .describe('Search text to apply to the on-screen contacts list; empty clears the search'),
})

export const createContactSchema = z.object({
  number: z.string().min(1).describe('E.164 phone number, e.g. +15551234567'),
  email: z.string().email().nullish(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
})

export const updateContactSchema = z.object({
  id: z.number().int().describe('Contact id to update'),
  number: z.string().optional(),
  email: z.string().email().optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
})

export const tagContactSchema = z.object({
  contact_id: z.number().int().describe('Contact id to tag'),
  tags: z
    .array(z.object({ id: z.number().int(), label: z.string() }))
    .min(1)
    .describe('Tags to apply; each needs an existing tag id and label'),
})

export const listTagsSchema = z.object({})

export const createTagSchema = z.object({
  label: z.string().min(1).describe('The label for the new tag'),
})

export const listInboxesSchema = z.object({})

export const sendMessageSchema = z.object({
  contact_id: z.number().int().describe('Contact id to message'),
  message: z.string().min(1).describe('SMS body to send'),
  team_id: z.number().int().describe('Sending inbox/team id (from list_inboxes)'),
})

export const openConversationWithContactSchema = z.object({
  // Coerce: ids may arrive as strings from prior tool output; accept both.
  contact_id: z.coerce
    .number()
    .int()
    .describe(
      'Contact id whose conversation to open (from search_contacts / search_contacts_list)'
    ),
  team_id: z.coerce
    .number()
    .int()
    .describe('Inbox/team id to open the conversation in (from list_inboxes)'),
})

export const listUnreadConversationsSchema = z.object({})

export const searchConversationsSchema = z.object({
  term: z.string().min(1).describe('Keyword to search across message content and contact names'),
  page: z.number().int().min(1).default(1),
  per_page: z.number().int().min(1).max(50).default(10),
})

export const openConversationSchema = z.object({
  conversation_id: z.coerce
    .number()
    .int()
    .describe(
      'Conversation id to open (from search_conversations / list_unread_conversations / filter_conversations_list)'
    ),
})

export const replyToConversationSchema = z.object({
  conversation_id: z
    .number()
    .int()
    .describe('Conversation id to reply to (from list_unread_conversations)'),
  message: z.string().min(1).describe('Reply text to send'),
})

export const listBroadcastNumbersSchema = z.object({})

export const openBroadcastCreateSchema = z.object({})

export const setBroadcastDetailsSchema = z.object({
  name: z.string().min(1).optional().describe('Broadcast name'),
  message: z.string().min(1).optional().describe('SMS body to send'),
})

export const setBroadcastAudienceSchema = z.object({
  tag_ids: z.array(z.number().int()).optional().describe('Audience: tag ids from list_tags'),
  contact_ids: z.array(z.number().int()).optional().describe('Explicit recipient contact ids'),
})

export const setBroadcastSenderSchema = z.object({
  // Coerce: ask_user_choice returns the chosen value as a string ("123"), so the agent
  // may pass a string number_id; coercion accepts it without a validation stall.
  number_id: z.coerce
    .number()
    .int()
    .describe('Sending number id (number_id from list_broadcast_numbers)'),
})

// The keys of the conversation-list filter DROPDOWN (features/DropdownFilter →
// FiltersStore), which is what filter_conversations_list drives. These are NOT
// the unified-inbox quick filters (assigned/unassigned/mentions/draft/calls) —
// those are a separate control (QuickFiltersMenuStore) the tool does not drive,
// so they must not appear here or the tool advertises filters it cannot open.
// Availability is still per-inbox: the handler gates each key via getItem.
export const CONVERSATION_LIST_FILTERS = [
  'open',
  'unread',
  'pending',
  'nocalls',
  'scheduled',
  'outbound',
  'closed',
] as const

export type ConversationListFilter = (typeof CONVERSATION_LIST_FILTERS)[number]

export const filterConversationsListSchema = z.object({
  filter: z
    .enum(CONVERSATION_LIST_FILTERS)
    .describe(
      'Which filter to apply to the on-screen conversation list. The value is the internal key; ' +
        'match it to what the user asks for by meaning: open (open conversations), ' +
        'unread (conversations with unread messages), ' +
        'pending (awaiting a reply — shown in the inbox as "Needs response"), ' +
        'nocalls (hide calls-only conversations — shown as "Exclude calls-only"), ' +
        'scheduled (conversations with a scheduled message), outbound (outbound conversations), ' +
        'closed (closed conversations). ' +
        'Not every filter exists in every inbox; if the chosen one is unavailable the tool says so.'
    ),
})

export const getOnboardingStatusSchema = z.object({})

export const getComplianceStatusSchema = z.object({})

// Help centre tools — see specs/006-help-center-access/
export const searchHelpCenterSchema = z.object({
  query: z
    .string()
    .trim()
    .min(1)
    .max(400)
    .describe('What to search the help center for — the user question or topic'),
})

export const readHelpArticleSchema = z.object({
  // Coerce: the id arrives as a string from search_help_center output.
  article_id: z.coerce.string().min(1).describe('Help article id from search_help_center'),
})

export const openHelpArticleSchema = z.object({
  article_id: z.coerce.string().min(1).describe('Help article id (from search_help_center)'),
  url: z
    .string()
    .url()
    .optional()
    .describe(
      'The article public URL (from search_help_center). Fallback for opening it in a new browser tab when the in-app messenger is unavailable.'
    ),
})

export const openSupportMessengerSchema = z.object({
  prefill: z
    .string()
    .max(1000)
    .optional()
    .describe('Optional text to pre-populate the support composer. Never sent automatically.'),
})
